<template>
  <div class="gallery-notes">
    <div class="header">
      <h2>返图笔记</h2>
      <div class="actions">
        <el-button @click="doImport" :loading="importing">从 data.js 导入</el-button>
        <el-button @click="doSyncStats" :loading="syncing">同步互动数</el-button>
        <el-button type="primary" @click="openCreate">新增笔记</el-button>
      </div>
    </div>

    <el-form :inline="true" class="filters">
      <el-form-item label="搜索">
        <el-input v-model="filters.q" placeholder="标题/作者/ID" clearable @clear="load" @keyup.enter="load" style="width:180px" />
      </el-form-item>
      <el-form-item label="来源">
        <el-select v-model="filters.source" placeholder="全部" style="width:120px" @change="onFilterChange">
          <el-option label="全部" value="__all__" />
          <el-option label="粉丝" value="fan" />
          <el-option label="官方" value="official" />
        </el-select>
      </el-form-item>
      <el-form-item label="类型">
        <el-select v-model="filters.kind" placeholder="全部" style="width:120px" @change="onFilterChange">
          <el-option label="全部" value="__all__" />
          <el-option label="图片" value="image" />
          <el-option label="视频" value="video" />
        </el-select>
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="filters.status" placeholder="全部" style="width:120px" @change="onFilterChange">
          <el-option label="全部" value="__all__" />
          <el-option label="已发布" value="published" />
          <el-option label="草稿" value="draft" />
        </el-select>
      </el-form-item>
      <el-form-item label="新建日期">
        <el-date-picker
          v-model="filters.created_date"
          type="date"
          placeholder="全部日期"
          value-format="YYYY-MM-DD"
          format="YYYY-MM-DD"
          clearable
          style="width:150px"
          @change="onFilterChange"
        />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="load">查询</el-button>
      </el-form-item>
    </el-form>

    <div v-loading="loading" class="notes-grid">
      <div v-for="row in items" :key="row.note_id" class="note-card">
        <div class="cover" @click="openPreview(row)">
          <img v-if="row.cover" :src="row.cover" :alt="row.title || ''" loading="lazy">
          <div v-else class="cover-empty">无封面</div>
          <span v-if="row.kind === 'video'" class="badge-kind">视频</span>
          <span class="badge-status" :class="row.status">{{ row.status === 'published' ? '已发布' : '草稿' }}</span>
        </div>
        <div class="body">
          <div class="title" :title="row.title">{{ row.title || '（无标题）' }}</div>
          <div class="meta">
            <span class="author">{{ row.author_name || '—' }}</span>
            <span class="src">{{ row.source === 'official' ? '官方' : '粉丝' }}</span>
          </div>
          <div class="date">新建 {{ formatCreatedDate(row.created_at) }}</div>
          <div class="stats">赞 {{ row.likes || 0 }} · 藏 {{ row.collects || 0 }} · 评 {{ row.comments || 0 }}</div>
          <div class="ops">
            <el-button link type="primary" @click="openEdit(row)">编辑</el-button>
            <el-button link type="primary" @click="openPreview(row)">预览</el-button>
            <el-button
              v-if="row.status === 'draft'"
              link
              type="success"
              @click="publishNote(row)"
            >上架</el-button>
            <el-button
              v-else
              link
              type="warning"
              @click="softDelete(row)"
            >下架</el-button>
            <el-button link type="danger" @click="hardDelete(row)">删除</el-button>
          </div>
        </div>
      </div>
      <div v-if="!loading && !items.length" class="empty">暂无笔记</div>
    </div>

    <div class="pager">
      <el-pagination
        background
        layout="total, prev, pager, next"
        :total="total"
        :page-size="filters.limit"
        :current-page="filters.page"
        @current-change="(p) => { filters.page = p; load() }"
      />
    </div>

    <el-drawer v-model="drawer" :title="form.note_id && editing ? '编辑笔记' : '新增笔记'" size="520px">
      <el-form label-width="90px">
        <el-form-item label="笔记ID">
          <el-input v-model="form.note_id" :disabled="!!editing" placeholder="可空，自动生成" />
        </el-form-item>
        <el-form-item label="标题">
          <el-input v-model="form.title" />
        </el-form-item>
        <el-form-item label="作者名">
          <el-input v-model="form.author_name" />
        </el-form-item>
        <el-form-item label="作者ID">
          <el-input v-model="form.author_id" />
        </el-form-item>
        <el-form-item label="来源">
          <el-select v-model="form.source">
            <el-option label="粉丝" value="fan" />
            <el-option label="官方" value="official" />
          </el-select>
        </el-form-item>
        <el-form-item label="类型">
          <el-select v-model="form.kind">
            <el-option label="图片" value="image" />
            <el-option label="视频" value="video" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="form.status">
            <el-option label="已发布" value="published" />
            <el-option label="草稿" value="draft" />
          </el-select>
        </el-form-item>
        <el-form-item label="封面路径">
          <el-input v-model="form.cover_path" placeholder="相对 gallery 媒体路径" />
        </el-form-item>
        <el-form-item label="视频路径">
          <el-input v-model="form.video_path" />
        </el-form-item>
        <el-form-item label="图集路径">
          <el-input
            v-model="mediaPathsText"
            type="textarea"
            :rows="3"
            placeholder="每行一个相对路径"
          />
        </el-form-item>
        <el-form-item label="小红书">
          <el-input v-model="form.xhs_url" />
        </el-form-item>
        <el-form-item label="赞/藏/评">
          <el-input-number v-model="form.likes" :min="0" />
          <el-input-number v-model="form.collects" :min="0" style="margin-left:8px" />
          <el-input-number v-model="form.comments" :min="0" style="margin-left:8px" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="save" :loading="saving">保存</el-button>
          <el-button @click="drawer=false">取消</el-button>
        </el-form-item>
      </el-form>
    </el-drawer>

    <el-dialog
      v-model="previewVisible"
      :title="preview?.title || '媒体预览'"
      width="min(860px, 92vw)"
      top="6vh"
      align-center
      destroy-on-close
      class="note-preview-dialog"
      @closed="preview = null"
    >
      <div v-if="preview" class="preview-body">
        <p class="preview-meta">
          <span>{{ preview.author_name || '—' }}</span>
          <span>·</span>
          <span>{{ preview.source === 'official' ? '官方' : '粉丝' }}</span>
          <a
            v-if="preview.xhs_url"
            :href="preview.xhs_url"
            target="_blank"
            rel="noopener"
            style="margin-left:12px"
          >小红书原帖</a>
        </p>
        <video
          v-if="preview.kind === 'video' && preview.video"
          :src="preview.video"
          :poster="preview.cover || undefined"
          controls
          playsinline
          class="preview-video"
        />
        <div v-else class="preview-imgs">
          <img
            v-for="(u, i) in previewMediaList"
            :key="i"
            :src="u"
            alt=""
            class="preview-img"
          >
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { reactive, ref, computed, onMounted, watch } from 'vue'
import axios from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useRoute } from 'vue-router'

export default {
  name: 'GalleryNotes',
  setup() {
    const route = useRoute()
    const loading = ref(false)
    const importing = ref(false)
    const syncing = ref(false)
    const saving = ref(false)
    const items = ref([])
    const total = ref(0)
    const drawer = ref(false)
    const editing = ref(false)
    const previewVisible = ref(false)
    const preview = ref(null)
    const mediaPathsText = ref('')
    // Element Plus 对 value="" 的选项常不渲染选中文案；用 __all__ 表示「全部」
    const ALL = '__all__'
    const filters = reactive({
      q: '',
      source: ALL,
      kind: ALL,
      status: ALL,
      created_date: '',
      author_id: route.query.author_id || '',
      page: 1,
      limit: 50
    })

    const formatCreatedDate = (v) => {
      if (!v) return '—'
      return String(v).slice(0, 10)
    }
    const form = reactive({
      note_id: '',
      title: '',
      author_id: '',
      author_name: '',
      source: 'fan',
      kind: 'image',
      status: 'published',
      cover_path: '',
      video_path: '',
      xhs_url: '',
      likes: 0,
      collects: 0,
      comments: 0
    })

    const previewMediaList = computed(() => {
      const p = preview.value
      if (!p) return []
      if (p.media && p.media.length) return p.media
      return p.cover ? [p.cover] : []
    })

    const filterParam = (v) => (v && v !== ALL ? v : undefined)

    const onFilterChange = () => {
      filters.page = 1
      load()
    }

    const load = async () => {
      loading.value = true
      try {
        const { data } = await axios.get('/api/gallery-admin/notes', {
          params: {
            q: filters.q || undefined,
            source: filterParam(filters.source),
            kind: filterParam(filters.kind),
            status: filterParam(filters.status),
            created_date: filters.created_date || undefined,
            author_id: filters.author_id || undefined,
            page: filters.page,
            limit: filters.limit
          }
        })
        items.value = data.data?.items || []
        total.value = data.data?.total || 0
      } catch (e) {
        ElMessage.error(e.response?.data?.message || '加载失败')
      } finally {
        loading.value = false
      }
    }

    const resetForm = () => {
      Object.assign(form, {
        note_id: '', title: '', author_id: '', author_name: '',
        source: 'fan', kind: 'image', status: 'published',
        cover_path: '', video_path: '', xhs_url: '',
        likes: 0, collects: 0, comments: 0
      })
      mediaPathsText.value = ''
    }

    const openCreate = () => {
      editing.value = false
      resetForm()
      drawer.value = true
    }

    const openEdit = async (row) => {
      editing.value = true
      try {
        const { data } = await axios.get(`/api/gallery-admin/notes/${row.note_id}`)
        const n = data.data
        Object.assign(form, {
          note_id: n.note_id,
          title: n.title,
          author_id: n.author_id || '',
          author_name: n.author_name || '',
          source: n.source,
          kind: n.kind,
          status: n.status,
          cover_path: n.cover_path || '',
          video_path: n.video_path || '',
          xhs_url: n.xhs_url || '',
          likes: n.likes || 0,
          collects: n.collects || 0,
          comments: n.comments || 0
        })
        mediaPathsText.value = (n.media_paths || []).join('\n')
        drawer.value = true
      } catch (e) {
        ElMessage.error('加载详情失败')
      }
    }

    const openPreview = async (row) => {
      try {
        const { data } = await axios.get(`/api/gallery-admin/notes/${row.note_id}`)
        preview.value = data.data
        previewVisible.value = true
      } catch (e) {
        ElMessage.error(e.response?.data?.message || '预览失败')
      }
    }

    const save = async () => {
      saving.value = true
      const payload = {
        ...form,
        media_paths: mediaPathsText.value.split('\n').map(s => s.trim()).filter(Boolean)
      }
      try {
        if (editing.value) {
          await axios.put(`/api/gallery-admin/notes/${form.note_id}`, payload)
        } else {
          await axios.post('/api/gallery-admin/notes', payload)
        }
        ElMessage.success('已保存')
        drawer.value = false
        load()
      } catch (e) {
        ElMessage.error(e.response?.data?.message || '保存失败')
      } finally {
        saving.value = false
      }
    }

    const softDelete = async (row) => {
      try {
        await ElMessageBox.confirm(
          `确认将「${row.title || row.note_id}」下架为草稿？前台将不再展示。`,
          '确认下架',
          { type: 'warning', confirmButtonText: '下架', cancelButtonText: '取消' }
        )
        await axios.delete(`/api/gallery-admin/notes/${row.note_id}`)
        ElMessage.success('已下架')
        load()
      } catch (e) {
        if (e === 'cancel' || e === 'close') return
        ElMessage.error(e.response?.data?.message || '下架失败')
      }
    }

    const publishNote = async (row) => {
      try {
        await ElMessageBox.confirm(
          `确认将「${row.title || row.note_id}」重新上架？`,
          '确认上架',
          { type: 'info', confirmButtonText: '上架', cancelButtonText: '取消' }
        )
        await axios.put(`/api/gallery-admin/notes/${row.note_id}`, { status: 'published' })
        ElMessage.success('已上架')
        load()
      } catch (e) {
        if (e === 'cancel' || e === 'close') return
        ElMessage.error(e.response?.data?.message || '上架失败')
      }
    }

    const hardDelete = async (row) => {
      try {
        await ElMessageBox.confirm(
          `确认永久删除「${row.title || row.note_id}」？此操作不可恢复。`,
          '确认删除',
          { type: 'error', confirmButtonText: '永久删除', cancelButtonText: '取消' }
        )
        await ElMessageBox.confirm(
          '再次确认：将硬删除该笔记及关联评价，无法撤销。',
          '二次确认',
          { type: 'error', confirmButtonText: '确认删除', cancelButtonText: '取消' }
        )
        await axios.delete(`/api/gallery-admin/notes/${row.note_id}`, { params: { hard: '1' } })
        ElMessage.success('已删除')
        load()
      } catch (e) {
        if (e === 'cancel' || e === 'close') return
        ElMessage.error(e.response?.data?.message || '删除失败')
      }
    }

    const doImport = async () => {
      importing.value = true
      try {
        const { data } = await axios.post('/api/gallery-admin/sync/import')
        ElMessage.success(`导入完成：笔记 ${data.data?.notes}，作者 ${data.data?.authors}`)
        load()
      } catch (e) {
        ElMessage.error(e.response?.data?.message || '导入失败')
      } finally {
        importing.value = false
      }
    }

    const doSyncStats = async () => {
      syncing.value = true
      try {
        const { data } = await axios.post('/api/gallery-admin/sync/stats', { limit: 0 })
        if (data.data?.ok) {
          ElMessage.success(data.data.message + (data.data.log ? `，日志 ${data.data.log}` : ''))
        } else {
          ElMessage.warning(data.data?.message || '未能启动')
        }
      } catch (e) {
        ElMessage.error(e.response?.data?.message || e.response?.data?.data?.message || '启动失败')
      } finally {
        syncing.value = false
      }
    }

    watch(
      () => route.query.author_id,
      (aid) => {
        filters.author_id = aid || ''
        filters.page = 1
        load()
      }
    )

    onMounted(load)
    return {
      loading, importing, syncing, saving, items, total, filters, form, drawer, editing,
      mediaPathsText, previewVisible, preview, previewMediaList, formatCreatedDate,
      load, onFilterChange, openCreate, openEdit, openPreview, save, softDelete, publishNote, hardDelete,
      doImport, doSyncStats
    }
  }
}
</script>

<style scoped>
.gallery-notes { padding: 8px 4px; }
.header { display:flex; justify-content:space-between; align-items:center; margin-bottom:12px; }
.header h2 { margin:0; font-size:18px; }
.actions { display:flex; gap:8px; flex-wrap:wrap; }
.filters { margin-bottom:12px; }
.filters :deep(.el-form-item__label) {
  color: #606266;
  font-weight: 500;
}
.filters :deep(.el-select .el-select__wrapper),
.filters :deep(.el-select .el-input__wrapper) {
  min-height: 32px;
}
.filters :deep(.el-select__selected-item),
.filters :deep(.el-select__placeholder),
.filters :deep(.el-input__inner) {
  color: #303133 !important;
  -webkit-text-fill-color: #303133;
  opacity: 1;
}
.notes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 14px;
  min-height: 120px;
}
.note-card {
  background: #fff;
  border: 1px solid #e8e4dc;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.cover {
  position: relative;
  aspect-ratio: 3 / 4;
  background: #f0ebe4;
  cursor: pointer;
  overflow: hidden;
}
.cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.cover-empty {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 12px;
}
.badge-kind {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0,0,0,.65);
  color: #fff;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
}
.badge-status {
  position: absolute;
  top: 8px;
  left: 8px;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f0f0f0;
  color: #666;
}
.badge-status.published {
  background: #e8f7ee;
  color: #1f8a4c;
}
.badge-status.draft {
  background: #f5f5f5;
  color: #888;
}
.body { padding: 10px 11px 12px; display: flex; flex-direction: column; gap: 6px; flex: 1; }
.title {
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 36px;
}
.meta {
  display: flex;
  justify-content: space-between;
  gap: 6px;
  font-size: 12px;
  color: #666;
}
.author {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.src { flex-shrink: 0; color: #a8322d; }
.date { font-size:11px; color:#aaa; margin-bottom:2px; }
.stats { font-size: 11px; color: #999; }
.ops {
  display: flex;
  flex-wrap: wrap;
  gap: 0 2px;
  margin-top: auto;
  padding-top: 4px;
  border-top: 1px dashed #eee;
}
.empty {
  grid-column: 1 / -1;
  text-align: center;
  color: #999;
  padding: 48px 16px;
}
.pager { margin-top: 16px; display: flex; justify-content: flex-end; }
.preview-body { max-height: 78vh; overflow: auto; }
.preview-meta { margin: 0 0 12px; font-size: 13px; color: #666; }
.preview-video {
  width: 100%;
  max-height: 70vh;
  background: #000;
  display: block;
  border-radius: 8px;
}
.preview-imgs { display: flex; flex-direction: column; gap: 10px; }
.preview-img {
  width: 100%;
  max-height: 70vh;
  object-fit: contain;
  background: #111;
  border-radius: 8px;
  display: block;
}
</style>
